"use client"

import {
  ChevronDown,
  ChevronUp,
  Edit05,
  RefreshCcw01,
  Trash01,
} from "@untitledui/icons"
import { motion } from "motion/react"

import { suitFor } from "@/components/collabs/conversation-suits"
import { Badge, type BadgeColor } from "@/components/ui/badge"
import { ButtonUtility } from "@/components/ui/button-utility"
import { cn } from "@/lib/utils"
import type { TrackPromptSummary } from "@/types/contents"

const CURATION_STATE_BADGE_COLOR: Record<
  TrackPromptSummary["curation_state"],
  BadgeColor<"pill-color">
> = {
  pending: "warning",
  locked: "gray-blue",
  filled: "success",
}

/**
 * PromptListsItemUpDownButton renders one reorderable prompt row — a copy
 * of PromptListsItemDragable with the drag handle swapped for up/down
 * buttons.
 * @param prompt - Prompt to render.
 * @param isFirst - Whether this is the first row (disables move-up).
 * @param isLast - Whether this is the last row (disables move-down).
 * @param animateLayout - Whether to animate this row sliding into position.
 * @param onMoveUp - Called when the move-up button is clicked.
 * @param onMoveDown - Called when the move-down button is clicked.
 * @param onEdit - Called when the edit button is clicked.
 * @param onRemove - Called when the remove button is clicked.
 * @param onRestore - Called when the restore button is clicked, undoing the soft delete on a prompt that has already been removed.
 * @returns The row.
 */
export function PromptListsItemUpDownButton({
  prompt,
  isFirst,
  isLast,
  isDisabled = false,
  animateLayout,
  onMoveUp,
  onMoveDown,
  onEdit,
  onRemove,
  onRestore,
}: {
  prompt: TrackPromptSummary
  isFirst: boolean
  isLast: boolean
  isDisabled?: boolean
  animateLayout: boolean
  onMoveUp: () => void
  onMoveDown: () => void
  onEdit: () => void
  onRemove: () => void
  onRestore?: () => void
}) {
  const isDeleted = Boolean(prompt.deleted_at)

  const softDeletedStyle = "opacity-50 grayscale-[35%]"

  return (
    <motion.div
      layout={animateLayout ? "position" : undefined}
      className={
        "border-secondary bg-primary relative flex items-center justify-between gap-4 rounded-xl border p-4 shadow-xs transition-shadow duration-200"
      }
    >
      <div
        className={cn("flex items-center gap-2", isDeleted && softDeletedStyle)}
      >
        <div className="-ml-1 flex w-6 shrink-0 flex-col self-stretch">
          <ButtonUtility
            size="xs"
            color="tertiary"
            icon={ChevronUp}
            tooltip="Move up"
            isDisabled={isFirst || isDisabled}
            onClick={onMoveUp}
          />
          <ButtonUtility
            size="xs"
            color="tertiary"
            icon={ChevronDown}
            tooltip="Move down"
            isDisabled={isLast || isDisabled}
            onClick={onMoveDown}
          />
        </div>
        <div className="flex flex-col">
          <div className="flex-item flex items-center gap-1.5">
            <span className="text-primary text-sm font-medium">
              {prompt.title}
            </span>
            <Badge
              color={CURATION_STATE_BADGE_COLOR[prompt.curation_state]}
              size="sm"
              type="pill-color"
              className="capitalize"
            >
              {prompt.curation_state}
            </Badge>
            {isDeleted && (
              <Badge color="gray" size="sm" type="pill-color">
                Removed
              </Badge>
            )}
          </div>
          <div className="flex-item flex flex-row gap-2">
            <div>
              {(() => {
                const suit = suitFor(prompt.content_type)
                const SuitIcon = suit.icon
                return <SuitIcon className="text-tertiary size-4 shrink-0" />
              })()}
            </div>
            <span className="text-tertiary text-xs whitespace-nowrap">
              {prompt.suggestions_count} suggestions
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <ButtonUtility
          tooltip="Edit"
          size="xs"
          icon={Edit05}
          isDisabled={isDeleted}
          onClick={onEdit}
        />
        {isDeleted ? (
          <ButtonUtility
            tooltip="Restore prompt"
            size="xs"
            icon={RefreshCcw01}
            onClick={onRestore}
          />
        ) : (
          <ButtonUtility
            tooltip="Remove prompt"
            size="xs"
            icon={Trash01}
            onClick={onRemove}
          />
        )}
      </div>
    </motion.div>
  )
}
